import React, { useState } from "react";
import axios from "axios";
import {
  Alert,
  AlertTriangle,
  Button,
  Card,
  CardHeader,
  CheckCircle,
  Checkbox,
  DatePicker,
  InfoCircle,
  Pencil01,
  Radio,
  RadioGroup,
  Select,
} from "@opendate-app/setlist";

import { useBroadcastEditContext } from "../BroadcastEditContainer";
import { audienceSummary } from "./InboxPreview";
import { senderLabel, smsChecklist } from "./smsMessage";
import { offsetDateFromTimeZone } from "../../../../shared/timeZoneLogic";
import { toastError } from "../../../../shared/toastHelper";

const moment = require("moment-timezone");

const TIME_ZONE_OPTIONS = moment.tz.names().map((zone) => ({ value: zone, label: zone }));

const styles = {
  stack: {
    display: "flex",
    flexDirection: "column",
    gap: 20,
    marginTop: 24,
  },
  checkRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "14px 24px",
    borderTop: "1px solid var(--sl-border-secondary)",
    fontSize: 14,
  },
  checkText: {
    flex: 1,
    minWidth: 0,
  },
  checkLabel: {
    fontWeight: 600,
    color: "var(--sl-text-primary)",
  },
  checkSummary: {
    color: "var(--sl-text-tertiary)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  muted: {
    fontSize: 14,
    color: "var(--sl-text-tertiary)",
  },
};

export const deliverySummary = ({ delivery, scheduledAt, formatDate, timeZone }) => {
  if (delivery === "now") return { ok: true, text: "Sends right away" };
  if (!scheduledAt) return { ok: false, text: "Pick a send date" };

  return { ok: true, text: formatDate(offsetDateFromTimeZone(scheduledAt, timeZone)) };
};

const ScheduleStep = ({ completed, delivery, setDelivery, setStep, rendered }) => {
  const {
    csrfToken,
    team,
    broadcast,
    updateBroadcast,
    recipientsCount,
    emailSendDomain,
    selectedSendDomain,
    subject,
    scheduledSend,
    setScheduledSend,
    scheduledAt,
    setScheduledAt,
    timeZone,
    setTimeZone,
    formatDate,
    isEmailTemplate,
    twilioMessagingServices,
  } = useBroadcastEditContext();

  const [transactional, setTransactional] = useState(Boolean(broadcast.transactional));

  const delivered = deliverySummary({ delivery, scheduledAt, formatDate, timeZone });

  const emailChecks = [
    {
      step: "recipients",
      label: "Recipients",
      ok: completed.recipients,
      text: completed.recipients
        ? `${recipientsCount} fans · ${audienceSummary(broadcast, true)}`
        : "Pick who should get this email",
    },
    {
      step: "sender",
      label: "Sender",
      ok: Boolean(emailSendDomain?.name && selectedSendDomain),
      text: emailSendDomain?.name && selectedSendDomain
        ? `${emailSendDomain.name} <${selectedSendDomain.full_from_address}>`
        : "Add a sender name and address",
    },
    {
      step: "sender",
      label: "Subject line",
      ok: Boolean(broadcast.subject),
      text: broadcast.subject || subject || "You need a subject line",
    },
    {
      step: "content",
      label: "Content",
      ok: completed.content,
      text: completed.content ? "Designed in the editor" : "Pick a starting point for your email",
    },
    { step: "schedule", label: "Delivery", ok: delivered.ok, text: delivered.text },
  ];
  const smsSender = twilioMessagingServices?.find((service) => service.id === broadcast.twilio_template?.twilio_messaging_service_id);
  const checks = isEmailTemplate
    ? emailChecks
    : smsChecklist({
      recipientsCount,
      audience: audienceSummary(broadcast, true),
      template: rendered?.text ?? broadcast.twilio_template?.template,
      senderName: smsSender && senderLabel(smsSender),
      delivered,
      messageErrors: rendered?.errors,
    });
  const toFix = checks.filter((check) => !check.ok).length;

  const cancelScheduledSend = () => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.put(`/teams/${team.id}/scheduled_sends/${scheduledSend.id}/cancel`)
      .then(() => {
        setScheduledSend(null);
        setScheduledAt(null);
      })
      .catch((error) => toastError((error.response?.data || ["Could not cancel the scheduled send"]).join(", ")));
  };

  return (
    <div style={styles.stack}>
      <RadioGroup name="broadcast-delivery" value={delivery} onChange={setDelivery}>
        <Radio value="now" label="Send now" />
        <Radio value="later" label="Send later" />
      </RadioGroup>

      {delivery === "later" && (
        <>
          <DatePicker
            label="Send date and time"
            placeholder="Pick a date and time"
            time
            min={new Date(new Date().setHours(0, 0, 0, 0))}
            value={scheduledAt}
            onChange={setScheduledAt}
          />
          <Select
            label="Time zone"
            searchable
            options={TIME_ZONE_OPTIONS}
            value={timeZone}
            onChange={setTimeZone}
          />
        </>
      )}

      {scheduledSend?.state === "scheduled" && (
        <div style={styles.muted}>
          Scheduled for {formatDate(scheduledSend.scheduled_at)}.{" "}
          <Button variant="link-destructive" size="sm" onClick={cancelScheduledSend}>Cancel scheduled send</Button>
        </div>
      )}

      <Card padding="none" header={<CardHeader title={toFix > 0 ? `Before you send · ${toFix} to fix` : "Ready to send"} />}>
        {checks.map((check) => {
          const Icon = check.info ? InfoCircle : check.ok ? CheckCircle : AlertTriangle;
          const iconColor = check.info ? "var(--sl-fg-quaternary)" : check.ok ? "var(--sl-fg-success)" : "var(--sl-fg-error)";

          return (
            <div key={check.label} style={styles.checkRow}>
              <Icon width={20} height={20} color={iconColor} />
              <div style={styles.checkText}>
                <div style={styles.checkLabel}>{check.label}</div>
                <div style={styles.checkSummary}>{check.text}</div>
              </div>
              {check.step !== "schedule" && !check.info && (
                <Button
                  variant="tertiary-color"
                  size="sm"
                  icon={Pencil01}
                  aria-label={`Edit ${check.label}`}
                  onClick={() => setStep(check.step)}
                />
              )}
            </div>
          );
        })}
      </Card>

      {!isEmailTemplate && (
        <Alert color="gray" title="Fans and carriers expect texts between 8am and 9pm local time. Schedule inside that window." />
      )}

      <div>
        <Checkbox
          id="broadcast-transactional"
          label="Transactional message"
          checked={transactional}
          onChange={(event) => {
            setTransactional(event.target.checked);
            updateBroadcast({ transactional: event.target.checked }, false);
          }}
        />
        <div style={{ ...styles.muted, marginTop: 4 }}>
          Also sends to unsubscribed fans. Follow{" "}
          <a href="https://en.wikipedia.org/wiki/Email_spam_legislation_by_country" target="_blank" rel="noopener noreferrer">
            local laws
          </a>.
        </div>
      </div>
    </div>
  );
};

export default ScheduleStep;
